import {
	Component,
	ElementRef,
	inject,
	OnDestroy,
	OnInit,
	output,
	Renderer2,
	viewChild,
	ChangeDetectionStrategy,
} from "@angular/core";
import { PageableDataSource } from "common/data/pageable-data-source.logic";
import { Subscription } from "rxjs";
import { DataViewComponent, Mode } from "common/data/data-view.component";
import { ActivatedRoute, Router } from "@angular/router";
import { MutexService } from "common/concurrency/mutex.service";
import { ChatbotResource } from "core/stages-client";
import { ChatHistoryStore } from "chatbot/menu/history/chat-history-store.component";
import { SessionStorageService } from "core/session-storage.service";
import { ChatbotComponent } from "chatbot/chatbot.component";
import { ChatHistoryService } from "chatbot/menu/history/chat-history.service";
import { ResponsiveDataViewComponent } from "common/data/responsive-data-view.component";
import { MenuComponent } from "common/menu/menu.component";
import ChatHistoryEntry = stages.chatbot.ChatHistoryEntry;

@Component({
	selector: "stages-chatbot-history",
	templateUrl: "./chatbot-history.component.html",
	styleUrls: ["./chatbot-history.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [ResponsiveDataViewComponent, MenuComponent],
})
export class ChatbotHistoryComponent implements OnInit, OnDestroy {
	private router = inject(Router);
	private route = inject(ActivatedRoute);
	private mutexService = inject(MutexService);
	private chatbotResource = inject(ChatbotResource);
	private sessionStorageService = inject(SessionStorageService);
	private chatHistoryService = inject(ChatHistoryService);
	private renderer = inject(Renderer2);

	ChatHistoryComponent = ChatbotHistoryComponent;
	dataSource!: PageableDataSource<ChatHistoryEntry>;
	private subscription = new Subscription();
	menuItems: MenuItem[] = [];
	page: number = 1;
	pageSize: number = 7;

	readonly chatHistoryRef = viewChild.required<ElementRef<HTMLDivElement>>("chatHistory");

	readonly dataViewRef = viewChild.required("dataView", { read: ElementRef });

	readonly dataView = viewChild.required<DataViewComponent<ChatHistoryEntry>>("dataView");

	readonly chatSelected = output<void>();

	readonly chatsDeleted = output<string[]>();

	ngOnInit(): void {
		this.dataSource = new PageableDataSource<ChatHistoryEntry>(
			new ChatHistoryStore(this.chatbotResource, this.chatHistoryService, this.pageSize),
			this.mutexService,
			this.router,
			this.route,
		);

		this.subscription = this.dataSource.data.subscribe((pagedResult) => {
			const securedPagedResult = pagedResult as SecuredPagedResult<ChatHistoryEntry>;
			this.page = securedPagedResult.page;

			this.menuItems = [
				{
					name: "chatbot.history.menu.show",
					iconClass: "ico ico-viewing",
					on: (chat: ChatHistoryEntry) => {
						this.openChat(chat);
					},
				},
				{
					name: "delete",
					iconClass: "ico ico-delete",
					on: async (chat: ChatHistoryEntry) => {
						await this.dataSource.delete([chat.guid], chat.guid);
						this.chatsDeleted.emit([chat.guid]);
					},
				},
			];
		});

		this.dataView().buttons = [
			{
				class: "button sm cancel",
				translate: "cancel",
				click: () => {
					this.dataView().mode = Mode.VIEW;
				},
				visible: () => {
					return this.dataView().mode === Mode.DELETE;
				},
				disabled: () => {
					return false;
				},
			},
			{
				class: "button sm delete",
				translate: "delete",
				click: () => {
					void this.dataView().dataSource.delete(this.dataView().selection.selected, "chats");
					this.dataView().mode = Mode.VIEW;
					this.chatsDeleted.emit(this.dataView().selection.selected);
				},
				visible: () => {
					return this.dataView().mode === Mode.DELETE;
				},
				disabled: () => {
					return this.dataView().selection.isEmpty();
				},
			},
		];
	}

	multiDelete(): void {
		this.dataView().enterDelete();
	}

	ngOnDestroy(): void {
		if (this.subscription) {
			this.subscription.unsubscribe();
		}
		if (this.dataSource) {
			this.dataSource.disconnect();
		}
	}

	openChat(chat: ChatHistoryEntry, $event?: UIEvent): void {
		this.sessionStorageService.setValue(ChatbotComponent.CHAT_ID_PREF_KEY, {
			chatGuid: chat.guid,
			viewId: chat.viewId,
		});
		// TODO: The 'emit' function requires a mandatory void argument
		this.chatSelected.emit();
		if ($event?.preventDefault) {
			$event.preventDefault();
			$event.stopPropagation();
		}
	}

	onHistoryHover(hovereing: boolean): void {
		const el = this.chatHistoryRef().nativeElement;
		const scrollbarWidth = el.offsetWidth - el.clientWidth;
		if (hovereing) {
			this.renderer.setStyle(this.dataViewRef().nativeElement, "margin-right", `-${scrollbarWidth}px`);
		} else {
			this.renderer.removeStyle(this.dataViewRef().nativeElement, "margin-right");
		}
	}

	static getGuid(chat: ChatHistoryEntry): string {
		return chat.guid;
	}

	static isDisabled(): boolean {
		return false;
	}
}
